import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, SimpleTable, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createAdminProductCategory, deleteAdminProductCategory, fetchAdminProductCategories, updateAdminProductCategory, } from '../../../api/ProductCategoryApi';
import { createProductCategoryColumns } from './tableColumns';
import styles from './ProductCategories.module.scss';
const EMPTY_FORM = {
    name: '',
    slug: '',
    description: '',
    header_title: '',
    header_description: '',
    parent: '',
    sort_order: '0',
    is_active: true,
};
const ProductCategories = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState([]);
    const latestRequestId = useRef(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formState, setFormState] = useState(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [headerImageFile, setHeaderImageFile] = useState(null);
    const [headerImagePreview, setHeaderImagePreview] = useState('');
    const [removeHeaderImage, setRemoveHeaderImage] = useState(false);
    const handleFieldChange = (field, value) => {
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }));
        if (validationErrors[field]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };
    const validateForm = () => {
        const errors = {};
        if (!formState.name.trim()) {
            errors.name = 'Category name is required';
        }
        return errors;
    };
    const loadCategories = useCallback(async (search) => {
        const requestId = ++latestRequestId.current;
        try {
            setLoading(true);
            const response = await fetchAdminProductCategories({ search: search.trim() || undefined });
            if (requestId !== latestRequestId.current) {
                return;
            }
            const rawData = response.body;
            const rows = Array.isArray(rawData) ? rawData : rawData.results || [];
            const transformed = rows.map((row) => ({
                id: row.id,
                name: row.name,
                slug: row.slug,
                description: row.description,
                header_title: row.headerTitle ?? row.header_title,
                header_description: row.headerDescription ?? row.header_description,
                header_image: row.headerImage ?? row.header_image,
                header_image_url: row.headerImageUrl ?? row.header_image_url,
                parent: row.parent,
                parent_id: row.parentId ?? row.parent_id,
                parent_name: row.parentName ?? row.parent_name,
                sort_order: row.sortOrder ?? row.sort_order ?? 0,
                is_active: !!(row.isActive ?? row.is_active),
                created_at: row.createdAt ?? row.created_at,
                updated_at: row.updatedAt ?? row.updated_at,
            }));
            setCategories(transformed);
        }
        catch (error) {
            console.error('Error loading categories:', error);
            showToast({ message: 'Failed to load categories', variant: 'error', noBorder: true });
            setCategories([]);
        }
        finally {
            if (requestId === latestRequestId.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadCategories(searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadCategories, searchTerm]);
    const openCreateModal = () => {
        setEditingCategory(null);
        setFormState(EMPTY_FORM);
        setValidationErrors({});
        setHeaderImageFile(null);
        setHeaderImagePreview('');
        setRemoveHeaderImage(false);
        setIsModalOpen(true);
    };
    const openEditModal = (category) => {
        setEditingCategory(category);
        setFormState({
            name: category.name || '',
            slug: category.slug || '',
            description: category.description || '',
            header_title: category.header_title || '',
            header_description: category.header_description || '',
            parent: category.parent || '',
            sort_order: String(category.sort_order ?? 0),
            is_active: category.is_active,
        });
        setValidationErrors({});
        setHeaderImageFile(null);
        setHeaderImagePreview(category.header_image_url || '');
        setRemoveHeaderImage(false);
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
        setFormState(EMPTY_FORM);
        setValidationErrors({});
        setHeaderImageFile(null);
        setHeaderImagePreview('');
        setRemoveHeaderImage(false);
    };
    const handleHeaderImageChange = (file) => {
        setHeaderImageFile(file);
        setRemoveHeaderImage(false);
        if (!file) {
            if (editingCategory?.header_image_url) {
                setHeaderImagePreview(editingCategory.header_image_url);
            }
            else {
                setHeaderImagePreview('');
            }
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setHeaderImagePreview(objectUrl);
    };
    const handleSave = async () => {
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setValidationErrors(formErrors);
            return;
        }
        setValidationErrors({});
        if (editingCategory && formState.parent && formState.parent === editingCategory.id) {
            showToast({ message: 'A category cannot be its own parent', variant: 'error', noBorder: true });
            return;
        }
        const payload = {
            name: formState.name.trim(),
            slug: formState.slug.trim() || undefined,
            description: formState.description.trim() || '',
            header_title: formState.header_title.trim() || '',
            header_description: formState.header_description.trim() || '',
            parent: formState.parent || null,
            sort_order: Number(formState.sort_order || 0),
            is_active: formState.is_active,
            ...(headerImageFile ? { header_image: headerImageFile } : {}),
            ...(removeHeaderImage ? { header_image: null } : {}),
        };
        setIsSaving(true);
        try {
            if (editingCategory) {
                const response = await updateAdminProductCategory(editingCategory.id, payload);
                if (response?.ok === false) {
                    const errorBody = await response.json().catch(() => ({}));
                    throw { response: { body: errorBody } };
                }
                showToast({ message: 'Category updated successfully', variant: 'success', noBorder: true });
            }
            else {
                const response = await createAdminProductCategory(payload);
                if (response?.ok === false) {
                    const errorBody = await response.json().catch(() => ({}));
                    throw { response: { body: errorBody } };
                }
                showToast({ message: 'Category created successfully', variant: 'success', noBorder: true });
            }
            closeModal();
            loadCategories(searchTerm);
        }
        catch (error) {
            const apiMessage = error?.response?.body?.detail ||
                error?.response?.body?.name?.[0] ||
                error?.response?.body?.slug?.[0] ||
                error?.response?.body?.non_field_errors?.[0] ||
                'Failed to save category';
            showToast({ message: apiMessage, variant: 'error', noBorder: true });
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleDelete = async (category) => {
        if (!window.confirm(`Delete category "${category.name}"?`)) {
            return;
        }
        try {
            await deleteAdminProductCategory(category.id);
            setCategories((prev) => prev.filter((row) => row.id !== category.id));
            showToast({ message: 'Category deleted', variant: 'success', noBorder: true });
        }
        catch (error) {
            console.error('Error deleting category:', error);
            showToast({ message: 'Failed to delete category', variant: 'error', noBorder: true });
        }
    };
    const parentOptions = useMemo(() => categories.filter((category) => !editingCategory || category.id !== editingCategory.id), [categories, editingCategory]);
    const columns = createProductCategoryColumns(openEditModal, handleDelete);
    return (_jsxs(AdminLayout, { children: [_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.tableWrapper, children: [loading && _jsx("div", { children: "Loading..." }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search categories", width: "100%", showIcon: true }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }), _jsx(SimpleTable, { columns: columns, data: categories, tableHeading: "Product Categories", rowHeight: "56px", hideTopBorder: true, enablePagination: true, pageSize: 20, paginationText: "Categories per page", onRowClick: (category) => openEditModal(category), customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Category", onClick: openCreateModal }) })] }) }), isModalOpen && (_jsx("div", { className: styles.modalBackdrop, onClick: closeModal, children: _jsxs("div", { className: styles.modal, onClick: (event) => event.stopPropagation(), children: [_jsx("h2", { className: styles.modalTitle, children: editingCategory ? 'Edit Category' : 'New Category' }), _jsxs("div", { className: styles.formGrid, children: [_jsxs("div", { className: validationErrors.name ? styles.fieldError : '', children: [_jsx(Input, { label: "Name", value: formState.name, onChange: (value) => handleFieldChange('name', value || ''), width: "100%" }), validationErrors.name && _jsx("p", { className: styles.errorText, children: validationErrors.name })] }), _jsx(Input, { label: "Slug", value: formState.slug, onChange: (value) => handleFieldChange('slug', value || ''), width: "100%", placeholder: "Optional - auto-generated if empty" }), _jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.fieldLabel, children: "Parent Category" }), _jsxs("select", { className: styles.select, value: formState.parent, onChange: (e) => handleFieldChange('parent', e.target.value), children: [_jsx("option", { value: "", children: "None (Top-Level)" }), parentOptions.map((category) => (_jsx("option", { value: category.id, children: category.parent_name ? `${category.parent_name} > ${category.name}` : category.name }, category.id)))] })] }), _jsx(Input, { label: "Sort Order", value: formState.sort_order, onChange: (value) => handleFieldChange('sort_order', value || '0'), width: "100%", type: "number" }), _jsxs("div", { className: `${styles.formField} ${styles.fullWidth}`, children: [_jsx("label", { className: styles.fieldLabel, children: "Description" }), _jsx("textarea", { className: styles.textarea, value: formState.description, onChange: (e) => handleFieldChange('description', e.target.value) })] }), _jsx(Input, { label: "Header Title", value: formState.header_title, onChange: (value) => handleFieldChange('header_title', value || ''), width: "100%" }), _jsxs("div", { className: `${styles.formField} ${styles.fullWidth}`, children: [_jsx("label", { className: styles.fieldLabel, children: "Header Description" }), _jsx("textarea", { className: styles.textarea, value: formState.header_description, onChange: (e) => handleFieldChange('header_description', e.target.value) })] }), _jsxs("div", { className: `${styles.formField} ${styles.fullWidth}`, children: [_jsx("label", { className: styles.fieldLabel, children: "Header Image" }), _jsxs("div", { className: styles.headerImageControls, children: [_jsx("input", { type: "file", accept: "image/*", onChange: (e) => handleHeaderImageChange(e.target.files?.[0] || null) }), !!headerImagePreview && (_jsxs("div", { className: styles.headerImagePreviewWrap, children: [_jsx("img", { src: headerImagePreview, alt: "Category header preview", className: styles.headerImagePreview }), _jsx(Button, { variant: "secondary", size: "small", label: "Remove image", onClick: () => {
                                                                setHeaderImageFile(null);
                                                                setHeaderImagePreview('');
                                                                setRemoveHeaderImage(true);
                                                            } })] }))] })] }), _jsxs("div", { className: `${styles.checkboxRow} ${styles.fullWidth}`, children: [_jsx("input", { id: "category-is-active", type: "checkbox", checked: formState.is_active, onChange: (e) => handleFieldChange('is_active', e.target.checked) }), _jsx("label", { htmlFor: "category-is-active", children: "Active" })] })] }), _jsxs("div", { className: styles.modalActions, children: [_jsx(Button, { variant: "secondary", size: "small", label: "Cancel", onClick: closeModal }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSaving ? 'Saving...' : 'Save', onClick: handleSave, disabled: isSaving })] })] }) }))] }));
};
export default ProductCategories;
